// OperationsConsole — Fase 15.10
// Overlay ativado por: Ctrl+Shift+P | ?ops=1 | localStorage[canteiro:ops]=1
import { useState, useEffect, useCallback } from "react";
import { generateOperationsReport } from "@/core/operations/operationsReport";
import { buildIncidentSummary } from "@/core/incidents/incidentRegistry";
import { buildBackupStatusReport } from "@/core/backup/backupOrchestrator";
import { getSafeguardSnapshot } from "@/core/safeguards/productionSafeguards";
import { runDeploymentValidation } from "@/core/deployment/deploymentValidator";
import { getLastHealthSnapshot } from "@/core/runtimeHealthMonitor";
import { getLastLeakReport } from "@/core/leak/memoryLeakDetector";
import { getChannelSnapshot } from "@/core/leak/channelMonitor";
import type { OperationsReport } from "@/core/operations/operationsReport";

type St = "ok" | "warn" | "err";
const COL = { ok: "#44ff88", warn: "#ffaa00", err: "#ff4444", lbl: "#888", bg: "#0a0a0f", card: "#111118", border: "#222233" };

function sc(s?: St): React.CSSProperties {
  return { color: s ? COL[s] : "#ccc", fontWeight: s ? 600 : 400 };
}

const rowS: React.CSSProperties = { display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12, borderBottom: "1px solid #1a1a2e" };
const cardS: React.CSSProperties = { background: COL.card, border: "1px solid " + COL.border, borderRadius: 8, padding: "12px 14px", minWidth: 280 };
const hdrS: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 };

function R({ label, value, s }: { label: string; value: string; s?: St }) {
  return <div style={rowS}><span style={{ color: COL.lbl }}>{label}</span><span style={sc(s)}>{value}</span></div>;
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return <div style={cardS}><div style={hdrS}>{title}</div>{children}</div>;
}

// ─── Coleta todos os dados fora do render ─────────────────────
interface OpsSnap {
  report: OperationsReport | null;
  healthOk: boolean | null;
  heapMB: number | null;
  leakRisk: string | null;
  openCh: number | null;
  zombies: number | null;
  emergencyStop: boolean;
  broadcastThrottled: boolean;
  realtimeStorm: boolean;
  rateLimits: number;
  violations: number;
  incTotal: number;
  incOpen: number;
  incCritical: number;
  incHigh: number;
  snapshots: number;
  lastSnapStatus: string;
  restorable: boolean;
  checkpoints: number;
  backupStale: boolean;
  depReady: boolean;
  depScore: number; // 0-100
  gates: { name: string; passed: boolean; reason?: string }[];
}

function collectSnap(): OpsSnap {
  const report = (() => { try { return generateOperationsReport(); } catch { return null; } })();
  const health = getLastHealthSnapshot();
  const leak   = getLastLeakReport();
  const chan   = getChannelSnapshot();
  const sg     = getSafeguardSnapshot();
  const inc    = buildIncidentSummary();
  const bak    = buildBackupStatusReport();
  const dep    = runDeploymentValidation();
  return {
    report,
    healthOk:          health ? health.overall === "ok" : null,
    heapMB:            leak?.heapMB ?? null,
    leakRisk:          leak?.overallRisk ?? null,
    openCh:            chan?.openCount ?? null,
    zombies:           chan ? chan.zombies.length : null,
    emergencyStop:     sg.emergencyStop,
    broadcastThrottled: sg.broadcastThrottle,
    realtimeStorm:     sg.realtimeStormDetected,
    rateLimits:        sg.rateLimits.length,
    violations:        sg.violations.length,
    incTotal:          inc.total,
    incOpen:           inc.open,
    incCritical:       inc.critical,
    incHigh:           inc.high,
    snapshots:         bak.totalSnapshots,
    lastSnapStatus:    bak.lastBackupStatus ?? "none",
    restorable:        bak.restorable,
    checkpoints:       bak.checkpoints,
    backupStale:       bak.isStale,
    depReady:          dep.passed,
    depScore:          dep.readinessScore,
    gates:             dep.gates.map(g => ({ name: g.gate, passed: g.passed, reason: g.message })),
  };
}

function useVisible() {
  const [vis, setVis] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("ops") === "1" ||
           localStorage.getItem("canteiro:ops") === "1";
  });
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "P") {
        setVis((v) => {
          const next = !v;
          try { localStorage.setItem("canteiro:ops", next ? "1" : "0"); } catch { /* */ }
          return next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  return [vis, setVis] as const;
}

export function OperationsConsole() {
  const [vis, setVis] = useVisible();
  const [snap, setSnap] = useState<OpsSnap | null>(null);
  const [ts, setTs] = useState("");

  const refresh = useCallback(() => {
    setTimeout(() => {
      try { setSnap(collectSnap()); } catch { /* */ }
      setTs(new Date().toLocaleTimeString());
    }, 0);
  }, []);

  useEffect(() => {
    if (!vis) return;
    refresh();
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [vis, refresh]);

  function handleClose() {
    setVis(false);
    try { localStorage.setItem("canteiro:ops", "0"); } catch { /* */ }
  }

  if (!vis) return null;

  const s = snap;
  const cert = s?.report?.certification ?? "NOT READY";
  const certColor = cert === "MISSION CRITICAL READY" || cert === "PRODUCTION READY" ? COL.ok
    : cert === "STAGING READY" ? COL.warn : COL.err;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", fontFamily: "monospace", color: "#ccc", overflowY: "auto" }}>
      <div style={{ background: COL.bg, borderBottom: "1px solid " + COL.border, padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>Operations Console</span>
          <span style={{ marginLeft: 16, fontSize: 11, color: "#555" }}>Fase 15</span>
          <span style={{ marginLeft: 16, fontSize: 11, color: certColor, fontWeight: 700 }}>{cert}</span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#555" }}>{ts || "..."}</span>
          <button onClick={refresh} style={{ background: "#1a1a2e", border: "1px solid " + COL.border, color: "#ccc", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontSize: 12 }}>Refresh</button>
          <button onClick={handleClose} style={{ background: "#1a1a2e", border: "1px solid " + COL.border, color: "#ccc", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontSize: 12 }}>Fechar</button>
        </div>
      </div>

      {!s ? (
        <div style={{ padding: 40, textAlign: "center", color: "#555" }}>Coletando dados...</div>
      ) : (
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>

          <Card title="System Health">
            <R label="Status" value={s.healthOk === null ? "--" : s.healthOk ? "Saudavel" : "Degradado"} s={s.healthOk === null ? undefined : s.healthOk ? "ok" : "err"} />
            <R label="Score" value={s.report ? s.report.readinessScore + "%" : "--"} s={s.report && s.report.readinessScore >= 80 ? "ok" : s.report && s.report.readinessScore >= 50 ? "warn" : "err"} />
            <R label="Heap MB" value={s.heapMB !== null ? s.heapMB.toFixed(1) + " MB" : "--"} s={s.heapMB === null || s.heapMB < 200 ? "ok" : "warn"} />
            <R label="Leak Risk" value={s.leakRisk ?? "--"} s={!s.leakRisk || s.leakRisk === "low" || s.leakRisk === "none" ? "ok" : s.leakRisk === "medium" ? "warn" : "err"} />
            <R label="Open Channels" value={s.openCh !== null ? String(s.openCh) : "--"} s={s.openCh === null || s.openCh <= 20 ? "ok" : "warn"} />
            <R label="Zombie Channels" value={s.zombies !== null ? String(s.zombies) : "--"} s={s.zombies === null || s.zombies === 0 ? "ok" : "warn"} />
          </Card>

          <Card title="Environment">
            <R label="Stage" value={s.report?.environment ?? "--"} />
            <R label="Consistent" value={s.report?.sections.environment.consistent ? "Yes" : "No"} s={s.report?.sections.environment.consistent ? "ok" : "err"} />
            <R label="Issues" value={String(s.report?.sections.environment.issues.length ?? 0)} s={s.report?.sections.environment.issues.length ? "warn" : "ok"} />
          </Card>

          {/* incidentRegistry e backupOrchestrator são frameworks em memória
              (sem persistência) — rotulados como simulados para não passar
              falsa sensação de cobertura operacional real. */}
          <Card title="Incidents (simulado — em memória)">
            <R label="Total" value={String(s.incTotal)} />
            <R label="Open" value={String(s.incOpen)} s={s.incOpen > 0 ? "warn" : "ok"} />
            <R label="Critical" value={String(s.incCritical)} s={s.incCritical > 0 ? "err" : "ok"} />
            <R label="High" value={String(s.incHigh)} s={s.incHigh > 0 ? "warn" : "ok"} />
          </Card>

          <Card title="Backup (simulado — em memória)">
            <R label="Snapshots" value={String(s.snapshots)} />
            <R label="Last Status" value={s.lastSnapStatus} s={s.lastSnapStatus === "verified" ? "ok" : s.lastSnapStatus !== "none" ? "warn" : "err"} />
            <R label="Restorable" value={s.restorable ? "Yes" : "No"} s={s.restorable ? "ok" : "err"} />
            <R label="Checkpoints" value={String(s.checkpoints)} s={s.checkpoints > 0 ? "ok" : "warn"} />
            <R label="Stale" value={s.backupStale ? "Yes" : "No"} s={s.backupStale ? "warn" : "ok"} />
          </Card>

          <Card title="Runtime Metrics">
            <R label="Emergency Stop" value={s.emergencyStop ? "ACTIVE" : "Clear"} s={s.emergencyStop ? "err" : "ok"} />
            <R label="Broadcast" value={s.broadcastThrottled ? "Throttled" : "Normal"} s={s.broadcastThrottled ? "warn" : "ok"} />
            <R label="Realtime Storm" value={s.realtimeStorm ? "STORM" : "Normal"} s={s.realtimeStorm ? "err" : "ok"} />
            <R label="Rate Limits" value={String(s.rateLimits)} s={s.rateLimits > 100 ? "warn" : "ok"} />
            <R label="Violations" value={String(s.violations)} s={s.violations > 0 ? "warn" : "ok"} />
          </Card>

          <Card title="Deploy Status">
            <R label="Ready" value={s.depReady ? "Ready" : "NOT Ready"} s={s.depReady ? "ok" : "err"} />
            <R label="Score" value={s.depScore + "%"} s={s.depScore >= 80 ? "ok" : s.depScore >= 50 ? "warn" : "err"} />
            {s.gates.map((g) => <R key={g.name} label={g.name} value={g.passed ? "OK" : (g.reason ?? "Fail")} s={g.passed ? "ok" : "err"} />)}
          </Card>

          <Card title="Operations Score">
            <R label="Deploy Safety" value={(s.report?.deploySafety ?? 0) + "%"} s={(s.report?.deploySafety ?? 0) >= 80 ? "ok" : "warn"} />
            <R label="Rollback Score" value={(s.report?.rollbackScore ?? 0) + "%"} s={(s.report?.rollbackScore ?? 0) >= 80 ? "ok" : "warn"} />
            <R label="Backup Score" value={(s.report?.backupScore ?? 0) + "%"} s={(s.report?.backupScore ?? 0) >= 80 ? "ok" : "warn"} />
            <R label="Overall" value={s.report ? s.report.readinessScore + "%" : "--"} s={s.report && s.report.readinessScore >= 80 ? "ok" : s.report && s.report.readinessScore >= 50 ? "warn" : "err"} />
            <R label="Certification" value={cert} s={cert === "PRODUCTION READY" || cert === "MISSION CRITICAL READY" ? "ok" : cert === "STAGING READY" ? "warn" : "err"} />
          </Card>

        </div>
      )}
    </div>
  );
}
